import * as React from "react";
import { FindPlaceCandidate } from "../../apis/google";
import { SystemServiceCode, useAuthStore } from "../../stores";
import { useApi, useFilters, useNavigation } from "../../hooks";
import { useTranslation } from "react-i18next";
import type { TableColumn } from "../../components/Table";
import { getDashboardColumns } from "./columns";
import { APIs } from "../../apis";
import type { Product, ProductsResponse } from "../../apis/products";
import { NearbyPlaceType, ProductsParamsSortBy } from "../../apis/products";
import { addDays, format, startOfDay } from "date-fns";
import { DatePickerRangeValue } from "../../components";
import { SVGs } from "../../assets";
import { Constants, Tools } from "../../utils";
import { Tags } from "../../libs";
import {
  TagsEvent,
  TagsContentType,
  TagsMode,
} from "../../libs/tags";

export type NavigateToBookingTracking = {
  target: string;
  slotsCount: number;
};

const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_PAGE = 1;
const DEFAULT_RADIUS = 50;
const RADIUS_OPTIONS_KM = [5, 10, 25, 50, 100] as const;

export const nearbyPlaceOptions = {
  [NearbyPlaceType.parkingLot]: {
    icon: SVGs.ParkingLot,
  },
  [NearbyPlaceType.busStation]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.busStop]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.transitStation]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.trainStation]: {
    icon: SVGs.Train,
  },
};

// Legacy mock data removed (dataSource comes from API now).

export function useViewModel() {
  const user = useAuthStore((s) => s.user);
  const skeletonOnNextLoad = React.useRef(true);
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { fetch, data, loading } = useApi<ProductsResponse>();
  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const { results: dataSource = [], count: totalItems = 0 } =
    data?.data?.partnerProducts ?? {};
  const showSkeleton = loading && skeletonOnNextLoad.current;

  const navigateToBooking = React.useCallback(
    (data: Product, tracking?: NavigateToBookingTracking) => {
      if (tracking) {
        Tags.event(TagsEvent.SelectContent, {
          content_type: TagsContentType.NextAvailable,
          item_id: data.id,
          slots_count: tracking.slotsCount,
          target: tracking.target,
        });
      }
      Tags.event(TagsEvent.SelectItem, {
        ecommerce: Tags.buildEcommerce([Tools.productToAnalyticsItem(data)], {
          item_list_name: data.partner_service?.service?.name,
        }),
      });
      navigate(`/booking`, { data });
    },
    [navigate],
  );

  const defaultDateRange = React.useMemo(() => {
    const today = new Date();
    return {
      from: format(today, "yyyy-MM-dd"),
      to: format(addDays(today, 3), "yyyy-MM-dd"),
    };
  }, []);

  const minDate = React.useMemo(() => startOfDay(new Date()).toISOString(), []);

  const { filters, setFilter, isValid } = useFilters({
    defaultValues: {
      placeName: "",
      latitude: "",
      longitude: "",
      countryCode: "",
      sortBy: ProductsParamsSortBy.availability,
      serviceTypeId: (user?.systemServices ?? [])[0]?.id?.toString() ?? "",
      language: (user?.systemLanguages ?? [])[0]?.id?.toString() ?? "",
      dateFrom: defaultDateRange.from,
      dateTo: defaultDateRange.to,
      radius: DEFAULT_RADIUS,
    },
    validate: (params) => {
      const invalidDateRange = !Tools.isValidDateRange({
        from: params.dateFrom,
        to: params.dateTo,
        minDate,
      });
      const invalidCoords = !Tools.isValidCoords({
        latitude: params.latitude,
        longitude: params.longitude,
      });
      return {
        dateFrom: invalidDateRange,
        dateTo: invalidDateRange,
        latitude: invalidCoords,
        placeName: invalidCoords,
        countryCode: invalidCoords,
        longitude: invalidCoords,
        radius: !Tools.isOneOf(params.radius, RADIUS_OPTIONS_KM),
        sortBy: !Tools.isOneOf(
          params.sortBy,
          Object.values(ProductsParamsSortBy),
        ),
        serviceTypeId: !user?.systemServices?.some(
          (_) => _.id?.toString() === params.serviceTypeId,
        ),
        language: !user?.systemLanguages?.some(
          (_) => _.id?.toString() === params.language,
        ),
      };
    },
    modifyFilter: (name, value: any) => {
      skeletonOnNextLoad.current = name !== "sortBy";
      setPage(DEFAULT_PAGE);
      setPageSize(
        name === "sortBy"
          ? Math.max(dataSource.length, DEFAULT_PAGE_SIZE)
          : DEFAULT_PAGE_SIZE,
      );
      if (name === "sortBy") {
        Tags.event(TagsEvent.SortList, {
          screen: Constants.RoutePath.Dashboard,
          sort_by: String(value),
        });
      } else {
        Tags.event(TagsEvent.ApplyFilter, {
          screen: Constants.RoutePath.Dashboard,
          filter_type: name,
          value:
            name === "place"
              ? (value?.formattedAddress ?? value?.displayName?.text)
              : name === "dateRange"
                ? Tools.formatDateRange(value?.from, value?.to)
                : name === "serviceTypeId"
                  ? user?.systemServices?.find(
                      (s) => s.id?.toString() === value,
                    )?.name
                  : name === "language"
                    ? user?.systemLanguages?.find(
                        (s) => s.id?.toString() === value,
                      )?.name
                    : String(value),
        });
      }
      const searchPatch: Record<string, string> = {
        sortBy: ProductsParamsSortBy.availability,
      };
      switch (name) {
        case "sortBy":
          searchPatch.sortBy = value;
          break;
        case "dateRange":
          return {
            ...searchPatch,
            dateFrom: value?.from ?? "",
            dateTo: value?.to ?? "",
          };
        case "place":
          return {
            ...searchPatch,
            placeName:
              value?.formattedAddress ?? value?.displayName?.text ?? "",
            latitude: value?.location?.latitude?.toString() ?? "",
            longitude: value?.location?.longitude?.toString() ?? "",
            countryCode: Tools.getCountryCodeFromPlace(value),
          };
        default:
          searchPatch[name] = value;
      }
      return searchPatch;
    },
  });

  const service = React.useMemo(
    () =>
      (user?.systemServices ?? []).find(
        (s) => s.id?.toString() === filters.serviceTypeId,
      ),
    [filters.serviceTypeId, user?.systemServices],
  );
  const columns = React.useMemo(
    () =>
      getDashboardColumns({
        t,
        navigateToBooking,
        availableServices: user?.availableServices,
      }),
    [navigateToBooking, t, user?.availableServices],
  );

  const coords = React.useMemo(
    () =>
      filters.latitude && filters.longitude
        ? { latitude: filters.latitude, longitude: filters.longitude }
        : undefined,
    [filters.latitude, filters.longitude],
  );

  const isTelehealth = service?.code === SystemServiceCode.telehealth;

  const getData = React.useCallback(async () => {
    if (!isValid || !coords) return;
    try {
      const res = await fetch(async (prevRes) => {
        const res = await APIs.products.products({
          page,
          pageSize,
          serviceTypeId: filters.serviceTypeId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          latitude: Number(coords.latitude),
          longitude: Number(coords.longitude),
          sortBy: filters.sortBy,
          ...(!isTelehealth
            ? { radius: Number(filters.radius) }
            : {}),
        });
        const { results = [] } = res.data.partnerProducts;
        const appending = pageSize === DEFAULT_PAGE_SIZE && page > DEFAULT_PAGE;
        res.data.partnerProducts.results = appending
          ? [...(prevRes?.data?.partnerProducts?.results ?? []), ...results]
          : [...results];
        if (!appending && results.length) {
          Tags.ecommerce(
            TagsEvent.ViewItemList,
            results.map(Tools.productToAnalyticsItem),
            {
              item_list_name: user?.systemServices?.find(
                (s) => s.id?.toString() === filters.serviceTypeId,
              )?.name,
            },
          );
        }
        return res;
      });
      if (!res) return;
    } catch {
    } finally {
      skeletonOnNextLoad.current = false;
    }
  }, [
    fetch,
    page,
    pageSize,
    filters.dateFrom,
    filters.dateTo,
    filters.serviceTypeId,
    filters.sortBy,
    filters.radius,
    coords,
    isValid,
    isTelehealth,
    user?.systemServices,
  ]);

  React.useEffect(() => {
    void getData();
  }, [getData]);

  const onExpandRow = React.useCallback(
    (expanded: boolean, record: Product) => {
      if (expanded) {
        Tags.event(TagsEvent.ViewItem, {
          ecommerce: Tags.buildEcommerce([
            Tools.productToAnalyticsItem(record),
          ]),
        });
      }
    },
    [],
  );

  const onMapCoverageClick = React.useCallback(() => {
    Tags.event(TagsEvent.SelectContent, {
      content_type: TagsContentType.MapCoverage,
    });
  }, []);

  const onLoadMore = React.useCallback(() => {
    if (loading) return;
    Tags.event(TagsEvent.PaginateList, {
      screen: Constants.RoutePath.Dashboard,
      mode: TagsMode.LoadMore,
    });
    setPageSize(DEFAULT_PAGE_SIZE);
    setPage(Math.floor(dataSource.length / DEFAULT_PAGE_SIZE) + 1);
  }, [loading, dataSource.length]);

  return {
    ...filters,
    coords,
    dateRange: { from: filters.dateFrom, to: filters.dateTo },
    minDate,
    page: 1,
    pageSize: dataSource.length,
    t,
    columns,
    dataSource,
    service,
    navigateToBooking,
    totalItems,
    loading,
    showSkeleton,
    serviceOptions: (user?.systemServices ?? []).map((s) => ({
      value: s.id.toString(),
      label: s.name,
    })),
    serviceLanguageOptions: (user?.systemLanguages ?? []).map((s) => ({
      value: s.id.toString(),
      label: s.name,
    })),
    radiusOptions: RADIUS_OPTIONS_KM.map((km) => ({
      value: String(km),
      label: `${km} km`,
    })),
    isTelehealth,
    onLoadMore,
    onExpandRow,
    onMapCoverageClick,
    setFilter,
  };
}

export type DashboardCoords = { latitude: string; longitude: string };

export type DashboardViewModel = {
  // filters
  serviceTypeId: string;
  service?: { code?: SystemServiceCode | null; name?: string };
  placeName: string;
  latitude: string;
  longitude: string;
  coords?: DashboardCoords;
  countryCode: string;
  language: string;
  dateFrom: string;
  dateTo: string;
  dateRange: DatePickerRangeValue;
  minDate: string;
  sortBy: string;
  radius: number;
  radiusOptions: Array<{ value: string; label: string }>;
  isTelehealth: boolean;
  setFilter: (
    name:
      | "serviceTypeId"
      | "language"
      | "dateRange"
      | "sortBy"
      | "place"
      | "radius",
    value: string | DatePickerRangeValue | FindPlaceCandidate,
  ) => void;
  serviceOptions: Array<{ value: string; label: string }>;
  serviceLanguageOptions: Array<{
    value: string;
    label: string;
  }>;

  // table
  t: (key: string, options?: Record<string, unknown>) => string;
  navigateToBooking: (
    data: Product,
    tracking?: NavigateToBookingTracking,
  ) => void;
  columns: TableColumn<Product>[];
  dataSource: Product[];
  totalItems: number;
  loading: boolean;
  showSkeleton: boolean;
  page: number;
  pageSize: number;
  onLoadMore: () => void;
  onExpandRow: (expanded: boolean, record: Product) => void;
  onMapCoverageClick: () => void;
};
